import React from 'react';
import * as ReactRouterDom from 'react-router-dom';
import { Box, ListItem, ListItemButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// The installed react-router-dom typings do not declare Link, though the runtime exports it.
const Link = (ReactRouterDom as unknown as { Link: React.ElementType }).Link;

interface SettingRowProps {
  label: React.ReactNode;
  /** Current value, shown beside the chevron so the reader sees it before opening. */
  value?: React.ReactNode;
  /** A control (switch, button) that owns its own clicks; the row itself is not clickable. */
  action?: React.ReactNode;
  onClick?: () => void;
  to?: string;
}

/**
 * One settings row: label on the left, value / control on the right. A row with
 * `onClick` or `to` is a whole-row button with a chevron; a row with `action` is static.
 */
const SettingRow: React.FC<SettingRowProps> = ({ label, value, action, onClick, to }) => {
  const content = (
    <>
      <Typography variant="body1" sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
        {value !== undefined && (
          <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
            {value}
          </Typography>
        )}
        {action}
        {(onClick || to) && <ChevronRightIcon color="primary" sx={{ fontSize: 22, mr: -0.5 }} />}
      </Box>
    </>
  );

  const rowSx = { minHeight: 52, px: 1.5 };

  // A clickable row is a ListItemButton inside a ListItem, so the list stays
  // ul > li: ListItemButton on its own renders a div, which a screen reader
  // does not read as a list item.
  if (to || onClick) {
    return (
      <ListItem disablePadding divider>
        <ListItemButton {...(to ? { component: Link, to } : { onClick })} sx={rowSx}>
          {content}
        </ListItemButton>
      </ListItem>
    );
  }
  return (
    <ListItem divider sx={rowSx}>
      {content}
    </ListItem>
  );
};

export default SettingRow;
